import * as React from 'react';
import { useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { cn } from '@restworld/utils-common';

import { InputProps, SupportTextVariant, borderColorObject, borderColorObjectType } from './types';

const inputBaseStyle =
  'block w-full border disabled:cursor-not-allowed disabled:opacity-50 py-4 px-2 h-12 bg-neutral-10 rounded-lg border border-neutral-90 !text-base focus:border-primary-300 focus:ring-primary-300 focus:ring-1 focus:outline-none placeholder:!text-neutral-200 placeholder:truncate placeholder:text-base placeholder:font-normal hover:bg-neutral-30 disabled:bg-neutral-40';

export const TextInput = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    onChange,
    rules = {},
    id,
    control,
    hasLeftPadding = false,
    borderless,
    testId,
    isError,
    supportText,
    helperText,
    supportTextStyles,
    isFullwidth,
    label,
    secondaryLabel,
    supportTextVariant = 'default',
    icon: Icon,
    rightIcon: RightIcon,
    bordercolor,
    color,
    rightIconStyle,
    className,
    wrapperStyle,
    ...rest
  } = props;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (onChange) onChange(e.target.value);
      return e;
    },
    [onChange]
  );

  // `helperText` is the legacy name callers still pass (the worker wizard among them). Rendered as a
  // fallback for `supportText`, never both: without this it fell into `...rest` and was spread onto the
  // <input> as a helpertext="[object Object]" attribute, so the text never reached the user.
  const supportTextContent = supportText ?? helperText;
  const supportTextEl = supportTextContent ? (
    <SupportTextComponent isError={isError} supportTextStyles={supportTextStyles} variant={supportTextVariant}>
      {supportTextContent}
    </SupportTextComponent>
  ) : null;

  const labelEl = label ? (
    <label
      htmlFor={id}
      className={cn(
        'flex items-center justify-between text-neutral-700 font-medium text-base mb-2',
        isFullwidth && 'w-full'
      )}
    >
      <span>{label}</span>
      <span className="!text-sm font-normal text-neutral-100">{secondaryLabel || ''}</span>
    </label>
  ) : null;

  const inputClassName = cn(
    inputBaseStyle,
    Icon && 'pl-10',
    RightIcon && 'pr-10',
    color === 'failure' && 'bg-red-50 border-red-500',
    isFullwidth && 'w-full',
    borderless && 'border-0 bg-white focus-visible:border-0 focus-visible:ring-0 focus-visible:outline-0',
    bordercolor && borderColorObject[bordercolor],
    hasLeftPadding && 'pl-10',
    isError && '!bg-red-50 placeholder:text-red-700 text-red-700 border-red-500 placeholder:!text-red-700',
    className
  );

  const iconWrapper = Icon ? (
    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
      <Icon className="text-neutral-700 h-[20px] w-[20px]" />
    </div>
  ) : null;

  const rightIconWrapper = RightIcon ? (
    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
      <RightIcon className={cn('text-neutral-700 h-[20px] w-[20px]', rightIconStyle)} />
    </div>
  ) : null;

  if (control)
    return (
      <>
        <div className={cn('relative w-full', wrapperStyle)}>
          {labelEl}
          <Controller
            name={id}
            control={control}
            rules={rules}
            render={({ field }) => (
              <div className="relative">
                {iconWrapper}
                <input data-cy={testId} className={inputClassName} {...field} {...rest} ref={ref} />
                {rightIconWrapper}
              </div>
            )}
          />
        </div>
        {supportTextEl}
      </>
    );

  return (
    <div className={cn('relative w-full', wrapperStyle)}>
      {labelEl}
      <div className="relative">
        {iconWrapper}
        <input onChange={handleChange} data-cy={testId} className={inputClassName} {...rest} ref={ref} />
        {rightIconWrapper}
      </div>
      {supportTextEl}
    </div>
  );
});

TextInput.displayName = 'TextInput';

export default TextInput;

function SupportTextComponent({
  isError,
  supportTextStyles,
  variant,
  children,
}: {
  supportTextStyles?: string;
  isError?: boolean;
  variant: SupportTextVariant;
  children: React.ReactNode;
}) {
  return (
    <p
      className={cn(
        '!text-sm font-normal mt-2',
        variant === 'success' ? 'text-green-500' : variant === 'error' ? 'text-red-500' : 'text-neutral-700',
        isError && 'text-red-500',
        supportTextStyles
      )}
    >
      {children}
    </p>
  );
}

export type { InputProps };
